import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert, Divider, Stack, Chip,
} from '@mui/material';
import { Print, PictureAsPdf, GridOn, Refresh } from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { reportIncomeStatementURL } from '../../BaseURL';

function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateLong = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'long', year: 'numeric' });
};

const NAVY   = '#0B2545';
const NAVY_2 = '#13315C';
const GREEN  = '#0F766E';
const RED    = '#B91C1C';
const GOLD   = '#C89211';
const RULE   = '#0B2545';
const SOFT   = '#F7F9FC';

export default function IncomeStatement({ start, end }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const printRef = useRef(null);

  const load = () => {
    if (!start || !end) return;
    setLoading(true); setError('');
    axios.get(reportIncomeStatementURL, {
      headers: headers(),
      params: { start, end },
    })
      .then(r => { if (r.data.success) setData(r.data); else setError(r.data.message); })
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  const showYtd = useMemo(
    () => !!(data && data.period.ytd_start && data.period.ytd_start !== data.period.start_date),
    [data]
  );

  // ─── PRINT ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open('', '_blank', 'width=1080,height=820');
    if (!w) return;
    w.document.write(`
      <html><head><title>Income Statement - ${data?.period.label || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 28px 32px; color: #111; }
        .letterhead { display: flex; align-items: center; gap: 20px; padding-bottom: 14px; border-bottom: 3px double ${NAVY}; }
        .letterhead img { width: 84px; height: 84px; object-fit: contain; border-radius: 6px; }
        .letterhead .info h1 { margin: 0; font-size: 26px; letter-spacing: 0.02em; color: ${NAVY}; }
        .letterhead .info p { margin: 3px 0 0; color: #555; font-size: 12px; }
        .stmt-title { text-align: center; margin: 22px 0 4px; }
        .stmt-title .name { font-size: 15px; font-weight: 800; letter-spacing: 0.16em; color: ${NAVY}; border-top: 1.5px solid ${NAVY}; border-bottom: 1.5px solid ${NAVY}; display: inline-block; padding: 4px 24px; }
        .stmt-title .period { margin-top: 8px; font-size: 12px; color: #555; }
        .stmt-title .ytd { display: inline-block; margin-top: 6px; padding: 3px 10px; background: ${NAVY}15; color: ${NAVY}; font-size: 11px; font-weight: 600; border-radius: 12px; }
        table { width: 100%; border-collapse: collapse; margin-top: 18px; }
        thead th { text-align: left; font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase; color: #fff; background: ${NAVY}; padding: 9px 12px; }
        thead th.right { text-align: right; }
        tbody td { padding: 6px 12px; font-size: 12.5px; border-bottom: 1px solid #eef0f4; }
        tbody td.right { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .section td { background: ${NAVY}; color: #fff; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; padding: 9px 12px; font-size: 11.5px; }
        .section.exp td { background: ${RED}; }
        .cat td { background: #f4f6fa; color: ${NAVY}; font-weight: 700; padding: 7px 12px; font-size: 12.5px; }
        .cat.exp td { background: #fcf0f0; color: #991B1B; }
        .item td { padding-left: 32px; }
        .sub td { border-top: 1px solid #d5dbe4; font-weight: 700; padding-left: 32px; }
        .sec-total td { border-top: 2px solid ${NAVY}; border-bottom: 2px solid ${NAVY}; font-weight: 800; color: ${NAVY}; background: #eef2f7; text-transform: uppercase; font-size: 12.5px; padding: 10px 12px; }
        .sec-total.exp td { border-color: #991B1B; color: #991B1B; background: #fcf0f0; }
        .net td { border-top: 3px double ${NAVY}; border-bottom: 3px double ${NAVY}; font-weight: 800; font-size: 14px; background: #fffdf5; padding: 12px 12px; text-transform: uppercase; letter-spacing: 0.08em; }
        .net.pos td { color: ${GREEN}; }
        .net.neg td { color: ${RED}; }
        .signatures { display: flex; gap: 40px; justify-content: space-between; margin-top: 60px; page-break-inside: avoid; }
        .sig { flex: 1; text-align: center; }
        .sig .line { border-top: 1px solid #333; margin-bottom: 6px; }
        .sig .role { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #555; font-weight: 700; }
        .footer { display: flex; justify-content: space-between; margin-top: 30px; font-size: 10px; color: #888; }
        @page { size: A4; margin: 15mm; }
      </style>
      </head><body>${printRef.current.innerHTML}</body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  // ─── EXPORT PDF ───────────────────────────────────────────────────────────
  const handlePdf = async () => {
    if (!data) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 42;

    // ── Letterhead ─────────────────────────────────────────────────────────
    let y = 40;
    const logoSize = 60;
    let textLeft = margin;
    if (data.church?.logo && String(data.church.logo).startsWith('data:image')) {
      try {
        const fmtType = data.church.logo.substring(11, data.church.logo.indexOf(';'));
        doc.addImage(data.church.logo, fmtType.toUpperCase(), margin, y, logoSize, logoSize);
        textLeft = margin + logoSize + 14;
      } catch (e) { /* logo failed to embed – skip silently */ }
    }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(11, 37, 69);
    doc.text(data.church?.name || 'Church', textLeft, y + 20);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(90);
    const subInfo = [data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ');
    if (subInfo) doc.text(subInfo, textLeft, y + 36);
    // Double navy rule under letterhead
    y += Math.max(logoSize, 42) + 8;
    doc.setDrawColor(11, 37, 69); doc.setLineWidth(1.2);
    doc.line(margin, y, pageW - margin, y);
    doc.setLineWidth(0.4); doc.line(margin, y + 3, pageW - margin, y + 3);
    y += 20;

    // ── Statement title ────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(11, 37, 69);
    const title = 'INCOME STATEMENT';
    const titleW = doc.getTextWidth(title);
    const tx = (pageW - titleW) / 2;
    doc.setLineWidth(0.8);
    doc.line(tx - 18, y - 4, tx + titleW + 18, y - 4);
    doc.text(title, pageW / 2, y + 12, { align: 'center' });
    doc.line(tx - 18, y + 18, tx + titleW + 18, y + 18);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(90);
    doc.text(
      `For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`,
      pageW / 2, y + 34, { align: 'center' }
    );
    y += 46;

    // ── Table ──────────────────────────────────────────────────────────────
    const body = [];
    const cols = showYtd ? 3 : 2;

    body.push([{ content: 'INCOME', colSpan: cols, styles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold' } }]);
    data.income.categories.forEach(cat => {
      body.push([{ content: cat.name, colSpan: cols, styles: { fillColor: [244, 246, 250], textColor: [11, 37, 69], fontStyle: 'bold' } }]);
      cat.items.forEach(it => {
        const row = [`      ${it.name}`, { content: fmt(it.amount), styles: { halign: 'right' } }];
        if (showYtd) row.push({ content: fmt(it.ytd_amount), styles: { halign: 'right' } });
        body.push(row);
      });
      const sub = [
        { content: `      Subtotal — ${cat.name}`, styles: { fontStyle: 'bold' } },
        { content: fmt(cat.total), styles: { halign: 'right', fontStyle: 'bold' } },
      ];
      if (showYtd) sub.push({ content: fmt(cat.ytd_total), styles: { halign: 'right', fontStyle: 'bold' } });
      body.push(sub);
    });
    const incT = [
      { content: 'TOTAL INCOME', styles: { fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
      { content: fmt(data.income.total), styles: { halign: 'right', fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
    ];
    if (showYtd) incT.push({ content: fmt(data.income.ytd_total), styles: { halign: 'right', fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } });
    body.push(incT);

    body.push([{ content: 'EXPENSES', colSpan: cols, styles: { fillColor: [185, 28, 28], textColor: 255, fontStyle: 'bold' } }]);
    data.expense.categories.forEach(cat => {
      body.push([{ content: cat.name, colSpan: cols, styles: { fillColor: [252, 240, 240], textColor: [153, 27, 27], fontStyle: 'bold' } }]);
      cat.items.forEach(it => {
        const row = [`      ${it.name}`, { content: fmt(it.amount), styles: { halign: 'right' } }];
        if (showYtd) row.push({ content: fmt(it.ytd_amount), styles: { halign: 'right' } });
        body.push(row);
      });
      const sub = [
        { content: `      Subtotal — ${cat.name}`, styles: { fontStyle: 'bold' } },
        { content: fmt(cat.total), styles: { halign: 'right', fontStyle: 'bold' } },
      ];
      if (showYtd) sub.push({ content: fmt(cat.ytd_total), styles: { halign: 'right', fontStyle: 'bold' } });
      body.push(sub);
    });
    const expT = [
      { content: 'TOTAL EXPENSES', styles: { fontStyle: 'bold', fillColor: [252, 240, 240], textColor: [153, 27, 27] } },
      { content: fmt(data.expense.total), styles: { halign: 'right', fontStyle: 'bold', fillColor: [252, 240, 240], textColor: [153, 27, 27] } },
    ];
    if (showYtd) expT.push({ content: fmt(data.expense.ytd_total), styles: { halign: 'right', fontStyle: 'bold', fillColor: [252, 240, 240], textColor: [153, 27, 27] } });
    body.push(expT);

    const netLabel = data.net_income >= 0 ? 'NET SURPLUS' : 'NET DEFICIT';
    const netColor = data.net_income >= 0 ? [15, 118, 110] : [185, 28, 28];
    const netRow = [
      { content: netLabel, styles: { fontStyle: 'bold', fillColor: [255, 253, 245], textColor: netColor, fontSize: 12 } },
      { content: fmt(Math.abs(data.net_income)), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 253, 245], textColor: netColor, fontSize: 12 } },
    ];
    if (showYtd) {
      const ytdC = data.net_income_ytd >= 0 ? [15, 118, 110] : [185, 28, 28];
      netRow.push({ content: fmt(Math.abs(data.net_income_ytd)), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 253, 245], textColor: ytdC, fontSize: 12 } });
    }
    body.push(netRow);

    const head = [[
      'Description',
      { content: `Period (GH₵)`, styles: { halign: 'right' } },
      ...(showYtd ? [{ content: `Year-to-Date (GH₵)`, styles: { halign: 'right' } }] : []),
    ]];

    autoTable(doc, {
      startY: y,
      head,
      body,
      theme: 'grid',
      styles: { fontSize: 9.5, cellPadding: 5, lineColor: [230, 232, 238], lineWidth: 0.4 },
      headStyles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold', halign: 'left', valign: 'middle' },
      columnStyles: showYtd
        ? { 0: { cellWidth: 'auto' }, 1: { cellWidth: 110, halign: 'right' }, 2: { cellWidth: 110, halign: 'right' } }
        : { 0: { cellWidth: 'auto' }, 1: { cellWidth: 150, halign: 'right' } },
      margin: { top: 40, left: margin, right: margin, bottom: 100 },
      didDrawPage: () => {
        doc.setFontSize(9); doc.setTextColor(120);
        const pageStr = `Page ${doc.internal.getNumberOfPages()}`;
        doc.text(pageStr, pageW - margin, pageH - 24, { align: 'right' });
        doc.text(`Generated ${new Date().toLocaleString('en-GH')}`, margin, pageH - 24);
      },
    });

    // ── Signatures (only on last page) ────────────────────────────────────
    let sy = doc.lastAutoTable?.finalY || y;
    sy += 44;
    if (sy > pageH - 110) { doc.addPage(); sy = 100; }
    const roles = ['Prepared by', 'Reviewed by', 'Approved by'];
    const colW  = (pageW - margin * 2) / roles.length;
    roles.forEach((role, i) => {
      const cx = margin + colW * i + 20;
      const cw = colW - 40;
      doc.setDrawColor(60); doc.setLineWidth(0.6);
      doc.line(cx, sy, cx + cw, sy);
      doc.setFontSize(9); doc.setTextColor(80); doc.setFont('helvetica', 'bold');
      doc.text(role.toUpperCase(), cx + cw / 2, sy + 14, { align: 'center' });
      doc.setFont('helvetica', 'normal'); doc.setTextColor(120);
      doc.text('Name & Signature', cx + cw / 2, sy + 28, { align: 'center' });
    });

    const stamp = `${data.period.start_date || start}_to_${data.period.end_date || end}`;
    doc.save(`Income_Statement_${stamp.replace(/[\/\s]/g, '_')}.pdf`);
  };

  // ─── EXPORT EXCEL ─────────────────────────────────────────────────────────
  const handleExcel = () => {
    if (!data) return;
    const rows = [];
    rows.push([data.church?.name || 'Church']);
    if (data.church?.address) rows.push([data.church.address]);
    const contact = [data.church?.phone, data.church?.email].filter(Boolean).join(' · ');
    if (contact) rows.push([contact]);
    rows.push([]);
    rows.push(['INCOME STATEMENT']);
    rows.push([`For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`]);
    rows.push([]);
    const headerRow = ['Description', 'Period (GH₵)'];
    if (showYtd) headerRow.push('Year-to-Date (GH₵)');
    rows.push(headerRow);

    rows.push(['INCOME']);
    data.income.categories.forEach(cat => {
      rows.push([cat.name]);
      cat.items.forEach(it => {
        const r = [`   ${it.name}`, Number(it.amount)];
        if (showYtd) r.push(Number(it.ytd_amount));
        rows.push(r);
      });
      const sub = [`Subtotal — ${cat.name}`, Number(cat.total)];
      if (showYtd) sub.push(Number(cat.ytd_total));
      rows.push(sub);
    });
    const incT = ['TOTAL INCOME', Number(data.income.total)];
    if (showYtd) incT.push(Number(data.income.ytd_total));
    rows.push(incT);

    rows.push([]);
    rows.push(['EXPENSES']);
    data.expense.categories.forEach(cat => {
      rows.push([cat.name]);
      cat.items.forEach(it => {
        const r = [`   ${it.name}`, Number(it.amount)];
        if (showYtd) r.push(Number(it.ytd_amount));
        rows.push(r);
      });
      const sub = [`Subtotal — ${cat.name}`, Number(cat.total)];
      if (showYtd) sub.push(Number(cat.ytd_total));
      rows.push(sub);
    });
    const expT = ['TOTAL EXPENSES', Number(data.expense.total)];
    if (showYtd) expT.push(Number(data.expense.ytd_total));
    rows.push(expT);

    rows.push([]);
    const netLabel = data.net_income >= 0 ? 'NET SURPLUS' : 'NET DEFICIT';
    const netRow = [netLabel, Number(data.net_income)];
    if (showYtd) netRow.push(Number(data.net_income_ytd));
    rows.push(netRow);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = showYtd
      ? [{ wch: 44 }, { wch: 18 }, { wch: 18 }]
      : [{ wch: 44 }, { wch: 20 }];
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = 1; C <= (showYtd ? 2 : 1); C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[addr];
        if (cell && typeof cell.v === 'number') cell.z = '#,##0.00';
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Income Statement');
    const stamp = `${data.period.start_date || start}_to_${data.period.end_date || end}`;
    XLSX.writeFile(wb, `Income_Statement_${stamp.replace(/[\/\s]/g, '_')}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Building income statement for {fmtDateLong(start)} — {fmtDateLong(end)}…
        </Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error" onClose={() => setError('')}>{error}</Alert>;
  if (!data) return null;

  const hasAnyData =
    data.income.categories.length > 0 || data.expense.categories.length > 0;

  return (
    <Box>
      {/* Action bar */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" mb={2} flexWrap="wrap">
        <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        <Button size="small" variant="outlined" startIcon={<Print />} onClick={handlePrint} disabled={!hasAnyData}>Print</Button>
        <Button size="small" variant="outlined" startIcon={<GridOn />} onClick={handleExcel} disabled={!hasAnyData}
          sx={{ color: '#0F6C3D', borderColor: '#0F6C3D', '&:hover': { borderColor: '#0F6C3D', bgcolor: '#0F6C3D0d' } }}>
          Export Excel
        </Button>
        <Button size="small" variant="contained" startIcon={<PictureAsPdf />} onClick={handlePdf} disabled={!hasAnyData}
          sx={{ bgcolor: RED, '&:hover': { bgcolor: '#991B1B' } }}>
          Export PDF
        </Button>
      </Stack>

      {/* Statement paper */}
      <Paper
        elevation={2}
        sx={{
          border: '1px solid #E2E8F0', borderRadius: 2,
          p: { xs: 3, md: 6 }, bgcolor: '#fff',
          maxWidth: 1040, mx: 'auto',
          boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
        }}
      >
        <Box ref={printRef}>
          {/* Letterhead */}
          <Box className="letterhead" sx={{
            display: 'flex', alignItems: 'center', gap: 3,
            pb: 2, borderBottom: `3px double ${RULE}`,
          }}>
            {data.church?.logo ? (
              <Box component="img" src={data.church.logo} alt="Logo"
                sx={{ width: 90, height: 90, objectFit: 'contain', borderRadius: 1 }} />
            ) : (
              <Box sx={{
                width: 90, height: 90, borderRadius: 1,
                bgcolor: `${NAVY}12`, color: NAVY,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 30,
              }}>
                {(data.church?.name || 'C').charAt(0).toUpperCase()}
              </Box>
            )}
            <Box className="info" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" fontWeight={800}
                sx={{ color: NAVY, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                {data.church?.name || 'Church'}
              </Typography>
              {(data.church?.address || data.church?.phone || data.church?.email) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {[data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ')}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Title block */}
          <Box className="stmt-title" sx={{ textAlign: 'center', mt: 3.5, mb: 0.5 }}>
            <Typography className="name" sx={{
              fontSize: 15, fontWeight: 800, letterSpacing: '0.16em', color: NAVY,
              borderTop: `1.5px solid ${NAVY}`, borderBottom: `1.5px solid ${NAVY}`,
              display: 'inline-block', px: 3, py: 0.5,
            }}>
              INCOME STATEMENT
            </Typography>
            <Typography className="period" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              For the period <b>{fmtDateLong(data.period.start_date)}</b> to <b>{fmtDateLong(data.period.end_date)}</b>
            </Typography>
            {showYtd && (
              <Box className="ytd" component="span" sx={{
                display: 'inline-block', mt: 1, px: 1.5, py: 0.3,
                bgcolor: `${NAVY}15`, color: NAVY, fontSize: 11, fontWeight: 700,
                borderRadius: 12, letterSpacing: '0.04em',
              }}>
                Year-to-date shown alongside · {data.period.ytd_label || ''}
              </Box>
            )}
          </Box>

          {/* Statement body */}
          {!hasAnyData ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="subtitle1" fontWeight={700}>No transactions for this period</Typography>
              <Typography variant="body2">There are no income entries or payments to report.</Typography>
            </Box>
          ) : (
            <StatementTable data={data} showYtd={showYtd} />
          )}

          {/* Signature block */}
          {hasAnyData && (
            <Box className="signatures" sx={{
              display: 'flex', gap: { xs: 3, md: 5 }, mt: 8,
              justifyContent: 'space-between', pageBreakInside: 'avoid',
            }}>
              {['Prepared by', 'Reviewed by', 'Approved by'].map(role => (
                <Box key={role} className="sig" sx={{ flex: 1, textAlign: 'center' }}>
                  <Box className="line" sx={{ borderTop: '1px solid #333', mb: 0.8 }} />
                  <Typography className="role" sx={{
                    fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: '#555', fontWeight: 700,
                  }}>
                    {role}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>Name &amp; Signature</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Footer */}
          <Box className="footer" sx={{
            display: 'flex', justifyContent: 'space-between', mt: 4,
            fontSize: 10, color: '#888',
          }}>
            <span>Generated {new Date().toLocaleString('en-GH')}</span>
            <span>{data.church?.name || ''}</span>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Inner rendered table ──────────────────────────────────────────────────
function StatementTable({ data, showYtd }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 18 }}>
      <thead>
        <tr>
          <th style={thStyle}>Description</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Period (GH₵)</th>
          {showYtd && <th style={{ ...thStyle, textAlign: 'right' }}>Year-to-Date (GH₵)</th>}
        </tr>
      </thead>
      <tbody>
        {/* INCOME */}
        <tr className="section"><td colSpan={showYtd ? 3 : 2} style={sectionHeadStyle(NAVY)}>INCOME</td></tr>
        {data.income.categories.map(cat => (
          <React.Fragment key={`inc-${cat.name}`}>
            <tr className="cat">
              <td colSpan={showYtd ? 3 : 2} style={catRowStyle(NAVY, '#f4f6fa')}>{cat.name}</td>
            </tr>
            {cat.items.map((it, i) => (
              <tr key={`inc-${cat.name}-${i}`} className="item">
                <td style={itemStyle}>{it.name}</td>
                <td style={amountStyle}>{fmt(it.amount)}</td>
                {showYtd && <td style={amountStyle}>{fmt(it.ytd_amount)}</td>}
              </tr>
            ))}
            <tr className="sub">
              <td style={subtotalLabelStyle}>Subtotal — {cat.name}</td>
              <td style={subtotalAmountStyle}>{fmt(cat.total)}</td>
              {showYtd && <td style={subtotalAmountStyle}>{fmt(cat.ytd_total)}</td>}
            </tr>
          </React.Fragment>
        ))}
        <tr className="sec-total">
          <td style={sectionTotalLabelStyle(NAVY, '#eef2f7')}>TOTAL INCOME</td>
          <td style={sectionTotalAmountStyle(NAVY, '#eef2f7')}>{fmt(data.income.total)}</td>
          {showYtd && <td style={sectionTotalAmountStyle(NAVY, '#eef2f7')}>{fmt(data.income.ytd_total)}</td>}
        </tr>

        <tr><td colSpan={showYtd ? 3 : 2} style={{ height: 16 }} /></tr>

        {/* EXPENSES */}
        <tr className="section exp"><td colSpan={showYtd ? 3 : 2} style={sectionHeadStyle(RED)}>EXPENSES</td></tr>
        {data.expense.categories.map(cat => (
          <React.Fragment key={`exp-${cat.name}`}>
            <tr className="cat exp">
              <td colSpan={showYtd ? 3 : 2} style={catRowStyle('#991B1B', '#fcf0f0')}>{cat.name}</td>
            </tr>
            {cat.items.map((it, i) => (
              <tr key={`exp-${cat.name}-${i}`} className="item">
                <td style={itemStyle}>{it.name}</td>
                <td style={amountStyle}>{fmt(it.amount)}</td>
                {showYtd && <td style={amountStyle}>{fmt(it.ytd_amount)}</td>}
              </tr>
            ))}
            <tr className="sub">
              <td style={subtotalLabelStyle}>Subtotal — {cat.name}</td>
              <td style={subtotalAmountStyle}>{fmt(cat.total)}</td>
              {showYtd && <td style={subtotalAmountStyle}>{fmt(cat.ytd_total)}</td>}
            </tr>
          </React.Fragment>
        ))}
        <tr className="sec-total exp">
          <td style={sectionTotalLabelStyle('#991B1B', '#fcf0f0')}>TOTAL EXPENSES</td>
          <td style={sectionTotalAmountStyle('#991B1B', '#fcf0f0')}>{fmt(data.expense.total)}</td>
          {showYtd && <td style={sectionTotalAmountStyle('#991B1B', '#fcf0f0')}>{fmt(data.expense.ytd_total)}</td>}
        </tr>

        <tr><td colSpan={showYtd ? 3 : 2} style={{ height: 16 }} /></tr>

        {/* NET */}
        <tr className={`net ${data.net_income >= 0 ? 'pos' : 'neg'}`}>
          <td style={netLabelStyle(data.net_income)}>
            {data.net_income >= 0 ? 'NET SURPLUS' : 'NET DEFICIT'}
          </td>
          <td style={netAmountStyle(data.net_income)}>{fmt(Math.abs(data.net_income))}</td>
          {showYtd && (
            <td style={netAmountStyle(data.net_income_ytd)}>{fmt(Math.abs(data.net_income_ytd))}</td>
          )}
        </tr>
      </tbody>
    </table>
  );
}

// ─── Inline styles ─────────────────────────────────────────────────────────
const thStyle = {
  textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
  color: '#fff', background: NAVY, padding: '10px 12px', fontWeight: 700,
};
const itemStyle    = { padding: '6px 12px', fontSize: 13.5, borderBottom: '1px solid #eef0f4', paddingLeft: 32, color: '#1e293b' };
const amountStyle  = { padding: '6px 12px', fontSize: 13.5, borderBottom: '1px solid #eef0f4',
                       textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                       fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#1e293b' };
const sectionHeadStyle = (color) => ({
  background: color, color: '#fff', fontWeight: 800, letterSpacing: '0.1em',
  padding: '9px 12px', fontSize: 12, textTransform: 'uppercase',
});
const catRowStyle = (fg, bg) => ({
  background: bg, color: fg, fontWeight: 700, padding: '7px 12px', fontSize: 13,
  borderBottom: '1px solid #e5e9ef',
});
const subtotalLabelStyle = {
  padding: '7px 12px', fontSize: 13, borderTop: '1px solid #d5dbe4',
  fontWeight: 700, paddingLeft: 32, color: '#0B2545',
};
const subtotalAmountStyle = {
  padding: '7px 12px', fontSize: 13, borderTop: '1px solid #d5dbe4',
  fontWeight: 700, textAlign: 'right', color: '#0B2545',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};
const sectionTotalLabelStyle = (fg, bg) => ({
  padding: '10px 12px', fontSize: 13, background: bg, color: fg, fontWeight: 800,
  borderTop: `2px solid ${fg}`, borderBottom: `2px solid ${fg}`, textTransform: 'uppercase',
});
const sectionTotalAmountStyle = (fg, bg) => ({
  padding: '10px 12px', fontSize: 13, background: bg, color: fg, fontWeight: 800,
  textAlign: 'right', borderTop: `2px solid ${fg}`, borderBottom: `2px solid ${fg}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
});
const netLabelStyle = (val) => ({
  padding: '14px 12px', fontSize: 15, fontWeight: 800, background: '#fffdf5',
  color: val >= 0 ? GREEN : RED,
  borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  textTransform: 'uppercase', letterSpacing: '0.1em',
});
const netAmountStyle = (val) => ({
  padding: '14px 12px', fontSize: 15, fontWeight: 800, textAlign: 'right', background: '#fffdf5',
  color: val >= 0 ? GREEN : RED,
  borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
});
